'use client';

import { useEffect, useRef, useState } from 'react';

import { useGlobal } from '@/lib/global-store';

declare global {
  interface Window {
    DMN?: {
      BookingFormHelper?: {
        widgets?: Array<{
          setNumberOfGuests: (guests: number | null) => void;
          setDate: (timestamp: number) => void;
          setTypeById: (type: string) => void;
          setVenueById: (venue: string) => void;
          goToStage: (stage: number) => void;
        }>;
        findBookingFormScriptTags?: () => void;
      };
    };
  }
}

export interface DMN_Attributes {
  venue: string;
  affiliateId?: string;
  hideOffers?: boolean;
  searchVenues?: boolean;
  customSource?: string;
  showTypeFirst?: boolean;
  hidePoweredBy?: boolean;
  allowedTypes?: string;
  googleTagManagerCode?: string;
  returnUrl?: string;

  // additional options
  numberOfGuests?: string;
  date?: string;

  // other
  className?: string;

  channelId?: string;
  containerId?: string;
}

export const CollinsWidget = ({
  venue,
  className,
  affiliateId,
  allowedTypes,
  customSource,
  googleTagManagerCode,
  hideOffers = true,
  hidePoweredBy = true,
  returnUrl,
  searchVenues,
  showTypeFirst,
  containerId,
  date,
  numberOfGuests,
  channelId,
}: DMN_Attributes) => {
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const { dmnConfig } = useGlobal();
  const [isClient, setIsClient] = useState(false);

  // these params are set by the tonic journey for specific events
  const scriptParams = {
    venue: dmnConfig.venue || venue,
    allowedTypes: dmnConfig.allowedTypes || allowedTypes,
    affiliateId: dmnConfig.affiliateId || affiliateId,
    date: dmnConfig.date || date,
    numberOfGuests: dmnConfig.numberOfGuests || numberOfGuests,
    searchVenues: dmnConfig.searchVenues || searchVenues,
    showTypeFirst: dmnConfig.showTypeFirst || showTypeFirst,
    hideOffers: dmnConfig.hideOffers || hideOffers,
    hidePoweredBy: dmnConfig.hidePoweredBy || hidePoweredBy,
    returnUrl: dmnConfig.returnUrl || returnUrl,
    customSource: dmnConfig.customSource || customSource,
    googleTagManagerCode: dmnConfig.googleTagManagerCode || googleTagManagerCode,
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (isClient && scriptRef.current === null) {
      const script = document.createElement('script');
      script.setAttribute('dmn-booking-form', '');
      script.src = '//widgets.designmynight.com/bookings-partner.min.js';

      // Set unchangeable widget attributes
      script.setAttribute('venue', scriptParams.venue);
      script.setAttribute('stylesheet', String(channelId));
      if (scriptParams.affiliateId) script.setAttribute('affiliate-id', scriptParams.affiliateId);
      if (scriptParams.allowedTypes) script.setAttribute('allowed-types', scriptParams.allowedTypes);
      script.setAttribute('custom-source', scriptParams.customSource || 'Own Website');
      if (scriptParams.googleTagManagerCode) {
        script.setAttribute('google-tag-manager-code', scriptParams.googleTagManagerCode);
      }
      if (scriptParams.hideOffers) script.setAttribute('hide-offers', String(scriptParams.hideOffers));
      if (scriptParams.hidePoweredBy) {
        script.setAttribute('hide-powered-by', String(scriptParams.hidePoweredBy));
      }
      if (scriptParams.returnUrl) script.setAttribute('return-url', scriptParams.returnUrl);
      if (scriptParams.searchVenues) {
        script.setAttribute('search-venues', String(scriptParams.searchVenues));
      }
      if (scriptParams.showTypeFirst) {
        script.setAttribute('show-type-first', String(scriptParams.showTypeFirst));
      }

      script.onload = () => {
        setScriptLoaded(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const window = globalThis as any;

        if (window.DMN?.BookingFormHelper) {
          window.DMN.BookingFormHelper.findBookingFormScriptTags();
        }
      };

      scriptRef.current = script;
      document.getElementById(containerId || 'dmn-container')?.appendChild(script);

      return () => {
        if (scriptRef.current && scriptRef.current.parentNode) {
          scriptRef.current.parentNode.removeChild(scriptRef.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClient]);

  useEffect(() => {
    if (scriptLoaded && window.DMN?.BookingFormHelper) {
      const widget = window.DMN.BookingFormHelper.widgets?.[0];

      if (widget) {
        // Helper function to safely set date
        const setWidgetDate = (dateString: string) => {
          try {
            const dateObj = new Date(dateString);
            if (!isNaN(dateObj.getTime())) {
              const timestamp = dateObj.getTime();
              widget.setDate(timestamp);
            } else {
              console.error(`Invalid date: ${dateString}`);
            }
          } catch (error) {
            console.error(`Error setting date: ${error}`);
          }
        };

        // Set number of guests
        if (scriptParams.numberOfGuests) {
          widget.setNumberOfGuests(parseInt(scriptParams.numberOfGuests));
        }

        // Set date
        if (scriptParams.date) {
          setWidgetDate(scriptParams.date);
        }
      } else {
        console.error('Widget not found in DMN.BookingFormHelper.widgets array');
      }
    } else {
      console.error('Script not loaded or DMN.BookingFormHelper not available yet');
    }
  }, [date, numberOfGuests, scriptLoaded, scriptParams.date, scriptParams.numberOfGuests]);

  return isClient && <div id={containerId || 'dmn-container'} className={className} />;
};
